import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, isDevMode, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BAD_REQUEST, FORBIDDEN, INTERNAL_SERVER_ERROR, NOT_FOUND, UNAUTHORIZED } from 'http-status-codes';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '@app/shared/services/authentication.service';

import { httpErrorPages } from './enums/http-error-pages';
import { errorTitle, httpValidationErrorMessages, unknownError } from './enums/http-validation-errors';
import { GlobalErrorHandlingService } from './global-error-handling.service';
import { ReportedError } from './reported-error.model';
import { errorObject } from 'rxjs/internal-compatibility';
import { UserAuthority } from '@app/domain/model/user-authority';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  constructor(private readonly injector: Injector,
              private authenticator: AuthenticationService) {
  }

  handleError(error: Error | HttpErrorResponse) {
    if (error instanceof HttpErrorResponse) {
      if (!navigator.onLine) {
        // Handle offline error
      } else {
        this.handleHttpError(error);
      }
    } else {
      this.handleClientError(error);
    }

    if (isDevMode()) {
      console.error(error);
    }
  }

  private handleHttpError(error: HttpErrorResponse) {
    const ngZone = this.injector.get(NgZone);
    const router = this.injector.get(Router);

    switch (error.status) {
      case UNAUTHORIZED:
        const ngbModal = this.injector.get(NgbModal);
        ngbModal.dismissAll();

        if(this.authenticator.isLoggedIn()){
          if(this.authenticator.getCurrentUser().hasAuthority(UserAuthority.DEALER_INFO)){
            ngZone.run(() => router.navigate([`/admin-login`]));
          } else {
            ngZone.run(() => router.navigate([`/login`]));
          }
          this.authenticator.clearSession();
        } else {
          ngZone.run(() => router.navigate([`/${httpErrorPages.unauthorized.path}`]));
        }
        break;
      case BAD_REQUEST:
        this.handleBackendValidationErrors(error);
        break;
      case NOT_FOUND:
        ngZone.run(() => router.navigate([`/${httpErrorPages.notFound.path}`]));
        break;
      case FORBIDDEN:
        ngZone.run(() => router.navigate([`/${httpErrorPages.forbidden.path}`]));
        break;
      case INTERNAL_SERVER_ERROR:
        ngZone.run(() => router.navigate([`/${httpErrorPages.internalServerError.path}`]));
        break;

      default:
        ngZone.run(() => router.navigate([`/${httpErrorPages.otherError.path}`]));
    }
  }

  private handleClientError(error: Error) {
    this.reportErrorOnServer(error.stack);
  }

  private reportErrorOnServer(error: string) {
    const errorLogger = this.injector.get(GlobalErrorHandlingService);
    const dealerAuthentication = this.injector.get(AuthenticationService);

    errorLogger.reportErrorOnServer({
      userId: dealerAuthentication.getCurrentUser() ? dealerAuthentication.getCurrentUser().id : null,
      dealerId: dealerAuthentication.getCurrentUser() ? dealerAuthentication.getCurrentUser().dealerId : null,
      message: error
    } as ReportedError).subscribe();
  }

  private handleBackendValidationErrors(error: HttpErrorResponse) {
    const ngZone = this.injector.get(NgZone);
    const toastrService = this.injector.get(ToastrService);

    if(error.error && typeof error.error === 'object' && Array.isArray(error.error)){
      error.error.forEach(item => {
        const errorMessage = httpValidationErrorMessages.getMessage(item);

        if (errorMessage.startsWith(unknownError)) {
          this.reportErrorOnServer(errorMessage);
        }

        ngZone.run(() => toastrService.error(errorMessage, errorTitle));
      });
    }
  }

}
